import { Injectable } from '@angular/core';
import { OcrLayoutResult, OcrLineBox, OcrWordBox } from './ocr-layout.interface';

/**
 * Minimal shape of the Tesseract.js worker we rely on. Declared locally so the
 * rest of the app never imports Tesseract types statically (the library is
 * loaded lazily, on demand, and must stay out of the initial bundle).
 */
interface OcrWorker {
  recognize(image: string): Promise<{ data: { text: string } }>;
  terminate(): Promise<void>;
}

/**
 * On-demand OCR for image slides, powering PDF Invisible Mode.
 *
 * Responsibilities:
 *  - Extract the line/word layout (text + bbox) from a slide image via Tesseract.js.
 *  - Lazy-load Tesseract.js (`import('tesseract.js')`) so it never weighs on
 *    the initial bundle.
 *  - Reuse a single worker across calls (never one worker per click), with a
 *    bounded acquisition so a CDN failure can't hang the session.
 *  - Cache the layout in memory for the session, keyed by `SlideBGFileName`.
 *
 * Out of scope by design: persistence, IndexedDB, backend OCR, batch OCR.
 */
@Injectable({ providedIn: 'root' })
export class SlideTextService {
  /** Session cache for the line/word layout (PDF Invisible Mode text layer). */
  private readonly layoutCache = new Map<string, OcrLayoutResult>();

  /** Single reusable worker (created once, on the first OCR request). */
  private workerPromise: Promise<OcrWorker> | null = null;

  /** Max time a single recognition may take before we give up. */
  private readonly OCR_TIMEOUT_MS = 30_000;

  /**
   * Max time to acquire (download + initialize) the worker. Tesseract v5's
   * createWorker() swallows language/initialize download errors and leaves its
   * promise pending forever, so without this bound a single CDN problem would
   * hang the spinner for the entire session.
   */
  private readonly WORKER_INIT_TIMEOUT_MS = 45_000;

  /** OCR language. English only. */
  private readonly OCR_LANG = 'eng';

  /**
   * Returns the line/word layout (each with bbox) plus the image's natural size
   * and the recognition time, for the PDF text layer. Reuses the shared worker.
   */
  async extractLayout(key: string, imageUrl: string): Promise<OcrLayoutResult> {
    if (!key || !imageUrl) {
      throw new Error('IMAGE_NOT_FOUND');
    }

    const cached = this.layoutCache.get(key);
    if (cached) {
      return cached;
    }

    const worker = await this.getWorker();
    const source = this.normalizeUrl(imageUrl);

    const start = performance.now();
    const [recognition, size] = await Promise.all([
      this.withTimeout(worker.recognize(source), this.OCR_TIMEOUT_MS, 'OCR_TIMEOUT'),
      this.getImageSize(source),
    ]);
    const ocrMs = Math.round(performance.now() - start);

    // `data.lines` is the flattened tree (circularize.js); each line carries its
    // bbox and its `words` (each word with bbox + text).
    const rawLines: any[] = (recognition?.data as any)?.lines ?? [];
    const lines: OcrLineBox[] = rawLines.map((ln) => ({
      x0: ln?.bbox?.x0 ?? 0,
      y0: ln?.bbox?.y0 ?? 0,
      x1: ln?.bbox?.x1 ?? 0,
      y1: ln?.bbox?.y1 ?? 0,
      words: (ln?.words ?? []).map((w: any): OcrWordBox => ({
        text: w?.text ?? '',
        x0: w?.bbox?.x0 ?? 0,
        y0: w?.bbox?.y0 ?? 0,
        x1: w?.bbox?.x1 ?? 0,
        y1: w?.bbox?.y1 ?? 0,
      })),
    }));

    const result: OcrLayoutResult = {
      naturalWidth: size.width,
      naturalHeight: size.height,
      ocrMs,
      lines,
    };
    this.layoutCache.set(key, result);
    return result;
  }

  /** Loads the image only to read its natural pixel size (no CORS needed). */
  private getImageSize(url: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => reject(new Error('IMAGE_NOT_FOUND'));
      img.src = url;
    });
  }

  /**
   * Lazily creates (once) and returns the shared worker, bounded by an
   * acquisition timeout so a hung Tesseract download can never poison the
   * session (createWorker() never rejects on language/init failure).
   */
  private getWorker(): Promise<OcrWorker> {
    if (!this.workerPromise) {
      const creation = this.createWorker();
      this.workerPromise = creation;
      creation.catch(() => {
        if (this.workerPromise === creation) {
          this.workerPromise = null;
        }
      });
    }

    const pending = this.workerPromise;

    return this.withTimeout(pending, this.WORKER_INIT_TIMEOUT_MS, 'WORKER_INIT_TIMEOUT').catch(
      (error) => {
        if (this.workerPromise === pending) {
          this.workerPromise = null;
        }
        pending.then(
          (worker) => worker.terminate().catch(() => {}),
          () => {},
        );
        throw error;
      },
    );
  }

  /** Dynamic import keeps Tesseract.js out of the initial bundle. */
  private async createWorker(): Promise<OcrWorker> {
    const mod: any = await import('tesseract.js');
    // CommonJS/ESM interop: under the bundler the CJS exports can land on
    // `.default` instead of the namespace root, so `mod.createWorker` is
    // undefined. Pick whichever object actually exposes createWorker.
    const tesseract = typeof mod?.createWorker === 'function' ? mod : mod?.default;
    // Tesseract v5+: createWorker(lang) loads + initializes the worker.
    const worker = await tesseract.createWorker(this.OCR_LANG);
    return worker as OcrWorker;
  }

  /**
   * Avoids mixed-content failures: if the page is served over HTTPS, request
   * the image over HTTPS too (the image host supports both).
   */
  private normalizeUrl(url: string): string {
    if (
      typeof location !== 'undefined' &&
      location.protocol === 'https:' &&
      url.startsWith('http://')
    ) {
      return 'https://' + url.slice('http://'.length);
    }
    return url;
  }

  /** Rejects with `new Error(code)` if the promise does not settle in time. */
  private withTimeout<T>(promise: Promise<T>, ms: number, code: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error(code)), ms);
      promise.then(
        (value) => {
          clearTimeout(timer);
          resolve(value);
        },
        (error) => {
          clearTimeout(timer);
          reject(error);
        },
      );
    });
  }
}
